<template>
  <div v-if="!isCarousel" :class="$style['c-grid-card-section-desktop__grid']" :style="gridStyle">
    <GridCardSectionCard
      v-for="(card, index) in cards"
      :key="card.id || `desktop-card-${index}`"
      :card="card"
    />
  </div>

  <div v-else :class="$style['c-grid-card-section-desktop__carousel']">
    <Swiper
      :modules="modules"
      :slides-per-view="desktopColumnCount"
      :space-between="16"
      :navigation="navigation"
      :pagination="pagination"
      @slideChange="onSlideChange"
    >
      <SwiperSlide v-for="(card, index) in cards" :key="card.id || `desktop-slide-${index}`">
        <div
          :class="[
            $style['c-grid-card-section-desktop__slide'],
            isActiveVisible(index) ? $style['is-active'] : $style['is-inactive'],
          ]"
        >
          <GridCardSectionCard :card="card" />
        </div>
      </SwiperSlide>
    </Swiper>

    <CarouselPageNav
      :show="showNav"
      :prev-class="`gcs-desktop-prev-${instanceKey}`"
      :next-class="`gcs-desktop-next-${instanceKey}`"
      :pagination-class="`gcs-desktop-pagination-${instanceKey}`"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import CarouselPageNav from '@/sections/CarouselPageNav.vue';
import GridCardSectionCard from './GridCardSectionCard.vue';

const props = defineProps<{
  cards: any[];
  isCarousel: boolean;
  desktopColumnCount: number;
  instanceKey: string;
}>();

const modules = [Navigation, Pagination];
const activeIndex = ref(0);

const navigation = computed(() => ({
  prevEl: `.gcs-desktop-prev-${props.instanceKey}`,
  nextEl: `.gcs-desktop-next-${props.instanceKey}`,
}));

const pagination = computed(() => ({
  el: `.gcs-desktop-pagination-${props.instanceKey}`,
  clickable: true,
  bulletClass: 'gcs-swiper-dot',
  bulletActiveClass: 'is-active',
}));

const showNav = computed(() => props.cards.length > props.desktopColumnCount);

const gridStyle = computed(() => ({
  '--gcs-desktop-columns': String(props.desktopColumnCount),
}));

const onSlideChange = (swiper: any) => {
  activeIndex.value = swiper?.activeIndex || 0;
};

const isActiveVisible = (idx: number) =>
  idx >= activeIndex.value && idx < activeIndex.value + props.desktopColumnCount;
</script>

<style module lang="scss">
.c-grid-card-section-desktop__grid {
  display: grid;
  grid-template-columns: repeat(var(--gcs-desktop-columns, 2), minmax(0, 1fr));
  gap: 12px;
}

.c-grid-card-section-desktop__slide {
  transition:
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 300ms ease;
}

.c-grid-card-section-desktop__slide.is-active {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.c-grid-card-section-desktop__slide.is-inactive {
  opacity: 0.78;
  transform: translateY(8px) scale(0.98);
}

@media (max-width: 768px) {
  .c-grid-card-section-desktop__grid,
  .c-grid-card-section-desktop__carousel {
    display: none;
  }
}
</style>
